<template>
    <ReviewsList
        v-if="!showDetails"
        @edit="editTestimonial($event)"
        @add="createTestimonial"
        @confirm="confirmTestimonial($event)"
    />
    <ReviewDetails
        ref="reviewDetails"
        v-else
        v-model:testimonial="testimonialToEdit"
        :testimonialBackup="backupData"
        :mode="mode"
        @cancel="handleCancel"
        @save="handleSave"
    />
</template>

<script>
import ReviewsList from "./reviews-list/ReviewsList.vue";
import ReviewDetails from "./reviews-list/ReviewDetails.vue";
import { mapActions, mapMutations, mapState } from "vuex";
import { useBackupData } from "@/shared/utils/forms/useBackupData";
import { useSaveOrDropModal } from "@/store/useSaveOrDropModal";
import { makeEmptyReviewForWall } from "@/apps/love/model/Review";
import { useCleanQueryParams } from "@/shared/utils/router/useCleanQueryParams";
import { EventBus } from "@/shared/projects/event-bus";
import { useVuexSideEffect } from "@/store/useVuexSideEffect";
import { ref } from "vue";

const REVIEW_MODE = {
    none: "",
    create: "create",
    edit: "edit",
};

export default {
    components: {
        ReviewsList,
        ReviewDetails,
    },
    setup() {
        const mode = ref(REVIEW_MODE.none);
        const showDetails = ref(false);
        const { cleanQueryParams } = useCleanQueryParams();
        const { askToConfirmIfNeeded } = useSaveOrDropModal();
        const { backupData, setBackupValue } = useBackupData({});
        useVuexSideEffect({
            // bring back view if publish in meantime... (avoid "save changes" button being inconsistent)
            setPublished: () => {
                showDetails.value = false;
            },
        });
        return {
            backupData,
            setBackupValue,
            askToConfirmIfNeeded,
            cleanQueryParams,
            showDetails,
            mode,
        };
    },
    data() {
        return {
            testimonialToEdit: {},
            newTestimonial: {},
        };
    },
    mounted() {
        this.$watch(() => this.$route.query.open, this.handleRoutingModeChange, {
            immediate: true,
        });
    },
    computed: {
        ...mapState("love", ["wall", "testimonials", "hasChanges"]),
    },
    methods: {
        ...mapMutations("love", ["removeTestimonial", "addTestimonial", "changeTestimonial"]),
        ...mapActions("love", ["sendConfirmationRequest"]),
        ...mapActions("love/projects", ["saveSnapshot", "loadProject"]),
        ...mapActions("alert", ["showWarnMessage"]),
        async editTestimonial(testimonial) {
            await this.askIfChanges();
            this.mode = REVIEW_MODE.edit;
            this.testimonialToEdit = testimonial;
            this.showDetails = true;
            this.setBackupValue(this.testimonialToEdit);
        },
        async createTestimonial() {
            await this.askIfChanges();
            this.mode = REVIEW_MODE.create;
            this.addTestimonial(makeEmptyReviewForWall(this.wall));
            this.testimonialToEdit = this.testimonials[this.testimonials.length - 1];
            this.showDetails = true;
            this.setBackupValue(this.testimonialToEdit);
        },
        async confirmTestimonial(testimonial) {
            await this.askIfChanges();
            await this.editTestimonial(testimonial);
            await this.$nextTick();
            // Manually fire component instance method:
            this.$refs.reviewDetails.makeConfirmationRequest();
        },
        async askIfChanges() {
            return this.askToConfirmIfNeeded({
                showModal: this.hasChanges,
                onConfirmChanges: async () => this.saveSnapshot(),
                onDropChanges: async () => this.loadProject(this.wall.uuid),
            });
        },
        async handleSave({ confirmation, testimonial }) {
            this.showDetails = false;
            await this.saveSnapshot();
            if (confirmation.send) {
                const { email, content } = confirmation;
                await this.sendConfirmationRequest({ email, content, testimonial });
            }
            this.mode = "";
        },
        async handleCancel() {
            this.showDetails = false;
            if (this.mode === REVIEW_MODE.create) {
                await this.removeTestimonial(this.newTestimonial);
                this.newTestimonial = {};
            } else {
                this.changeTestimonial(this.backupData);
            }
            await this.saveSnapshot();
            this.mode = "";
        },
        async handleRoutingModeChange(value) {
            if (this.mode !== REVIEW_MODE.create && value === REVIEW_MODE.create) {
                this.createTestimonial();
                setTimeout(() => {
                    this.cleanQueryParams(["open"]);
                }, 100);
            }
            if (value === REVIEW_MODE.edit) {
                const { query } = this.$route;
                const testimonialId = +query.id;

                setTimeout(() => {
                    this.cleanQueryParams(["open", "id"]);
                }, 100);

                //if you are creating or editing other review
                if (
                    this.mode === REVIEW_MODE.create ||
                    (this.mode === REVIEW_MODE.edit && testimonialId !== this.testimonialToEdit.id)
                ) {
                    EventBus.$emit("quitReviewDetails");

                    this.showWarnMessage({
                        message: this.$t("love.reviews_list.unsaved_changes"),
                    });

                    return;
                }

                const testimonial = this.testimonials.find((x) => x.id === +testimonialId);
                if (testimonial) {
                    this.editTestimonial(testimonial);
                }
            }
        },
    },
};
</script>
